import { z } from "zod";
import { IAsyncDropdownModel } from "common/models/IDropdownModel";
import dayjs from "dayjs";
import { IGridPostModel } from "common/models/GridModels";

export const FineSchema = z.object({
  fineID: z.string().nullish(),
  fineHead: z
    .string()
    .min(1, "Enter Fine Type")
    .max(10, "Fine Type must not be greater than 10 characters"),
  issueID: z.string().nullish(),
  materialAccessionNoID: z.string().nullish(),
  memberID: z.string().nullish(),
  fineDate: z.date({
    message: "Select Fine Date",
    required_error: "Select Fine Date",
  }),
  actualAmount: z.number(),
  waivedAmount: z.number(),
  receivedAmount: z.number(),
  outstandingAmount: z.number(),
  description: z
    .string()
    .max(250, "Description must not be greater than 150 characters")
    .optional()
    .nullable(),
  remarks: z
    .string()
    .max(250, "Remarks must not be greater than 150 characters")
    .optional()
    .nullable(),
  memberCode: z.string().min(1, "Enter Member Code"),
});
export type IFine = z.infer<typeof FineSchema>;
export interface FineFormProps {
  fine?: IFine;
}
export interface IFineGridModel {
  fineID: string | number;
  fineHead: string;
  fineDate: Date;
  actualAmount: number;
  waivedAmount: number;
  receivedAmount: number;
  outstandingAmount: number;
  description: string;
  remarks?: string;
  memberCode: string;
  memberName: string;
  accessionNo: string;
  totalWaivedAmount: number;
  totalActualAmount: number;
  totalReceivedAmount: number;
  totalOutstandingAmount: number;
  dueDay: number;
}

export const IFineGridFilterSchema = z
  .object({
    materialAccessionNoID: z.string().nullish(),
    accessionNoValue: z.custom<IAsyncDropdownModel>().optional(),
    memberID: z.string().nullish(),
    memberCodeValue: z.custom<IAsyncDropdownModel>().optional(),
    fromDate: z.coerce
      .string()
      .transform((c) => (c === "null" ? null : c))
      .transform((c) => (c === "undefined" ? null : c))
      .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
        message: "Invalid date",
      }),
    toDate: z.coerce
      .string()
      .transform((c) => (c === "null" ? null : c))
      .transform((c) => (c === "undefined" ? null : c))
      .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
        message: "Invalid date",
      }),
    instituteID: z.coerce.string().nullish(),
    memberInstituteID: z.coerce.string().nullish(),
    departmentID: z.coerce.string().nullish(),
    memberDepartmentID: z.coerce.string().nullish(),
    memberTypeID: z.coerce.string().nullish(),
    materialTypeID: z.coerce.string().nullish(),
    admissionYearID: z.coerce.string().nullish(),
    amountFilterType: z.string().nullish(),
    waivedAmountType: z.coerce.number().nullish(),
    waivedAmount: z.coerce.number().nullish(),
    receivedAmountType: z.coerce.number().nullish(),
    receivedAmount: z.coerce.number().nullish(),
    outstandingAmountType: z.coerce.number().nullish(),
    outstandingAmount: z.coerce.number().nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.fromDate && data.toDate) {
      const from = dayjs(data.fromDate);
      const to = dayjs(data.toDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        ctx.addIssue({
          path: ["toDate"],
          message: "To Date must be greater than or equal to From Date",
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });


export type IFineGridFilter = z.infer<typeof IFineGridFilterSchema>;

export const FineCollectionFilterSchema = z.object({
  memberID: z.string().nullish(),
  memberCodeValue: z
    .custom<IAsyncDropdownModel>()
    .refine((c) => !!c, { message: "Enter Member Name / Member Code" }),
});

export type IFineCollectionGridFilter = z.infer<
  typeof FineCollectionFilterSchema
>;

export interface IFineCollectionListGridPostModel extends IGridPostModel {
  filter?: IFineCollectionGridFilter;
}

export type MemberFineListResult = {
  fineId: string | number;
  fineHead: string;
  issueNo: number;
  issueDate: Date;
  dueDate: Date;
  returnDate: Date;
  overDueDay: number;
  fineDate: Date;
  accessionNo: string;
  title: string;
  memberID: string | number;
  memberName: string;
  memberCode: string;
  actualAmount: number;
  waivedAmount: number;
  receivedAmount: number;
  outstandingAmount: number;
  description: string;
  remark: string;
  userName: string;
};

export const GenerateReceiptSchema = z
  .object({
    fineID: z.array(z.string()),
    memberID: z.string({ message: "Select fine" }).min(1, "Select fine"),
    totalAmount: z.coerce.number({ message: "Select fine" }),
    actionType: z.enum(["collect", "waive"]).default("collect"),
    remarks: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.actionType === "waive" && !values.remarks?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["remarks"],
        message: "Enter Remarks",
      });
    }
  });

export type IGenerateReceipt = z.infer<typeof GenerateReceiptSchema>;

export const IFineReportsFilterSchema = z.object({
  materialAccessionNoID: z.string().nullish(),
  accessionNoValue: z.custom<IAsyncDropdownModel>().optional(),
  memberID: z.string().nullish(),
  memberCodeValue: z.custom<IAsyncDropdownModel>().optional(),
  fromDate: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
  toDate: z.coerce
    .string()
    .transform((c) => (c === "null" ? null : c))
    .transform((c) => (c === "undefined" ? null : c))
    .refine((c) => (!c && c != null ? dayjs(c).isValid() : true), {
      message: "Invalid date",
    }),
  reportType: z.coerce.number().nullish(),
  instituteID: z.coerce.string().nullish(),
  memberInstituteID: z.coerce.string().nullish(),
  departmentID: z.coerce.string().nullish(),
  memberDepartmentID: z.coerce.string().nullish(),
  memberTypeID: z.coerce.string().nullish(),
  materialTypeID: z.coerce.string().nullish(),
  admissionYearID: z.coerce.string().nullish(),
  amountFilterType: z.string().nullish(),
  waivedAmountType: z.coerce.number().nullish(),
  waivedAmount: z.coerce.number().nullish(),
  receivedAmountType: z.coerce.number().nullish(),
  receivedAmount: z.coerce.number().nullish(),
  outstandingAmountType: z.coerce.number().nullish(),
  outstandingAmount: z.coerce.number().nullish(),
});

export type IFineReportsFilter = z.infer<typeof IFineReportsFilterSchema>;

export type FineReportsCount = {
  actualAmount: number;
  waivedAmount: number;
  receivedAmount: number;
  outStandingAmount: number;
};
